import React from 'react';
import { Badge, Divider, Typography, Box, Button, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, Chip, Stack, Drawer, Tabs, Tab, Popper, Fade, Paper, Alert, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Link } from '@mui/material';
import { NotificationsNoneOutlined, Notifications, Info as InfoIcon, CheckCircle, WarningAmber, ShoppingCart, SupportAgent, LockReset, People, Close as CloseIcon, Refresh as RefreshIcon, Visibility as VisibilityIcon, MarkEmailRead as MarkEmailReadIcon, Sort as SortIcon, MarkEmailUnread as MarkEmailUnreadIcon, InfoOutlined, Clear as ClearIcon, AddShoppingCart } from '@mui/icons-material';
import SummarizeIcon from "@mui/icons-material/Summarize";
import { fetchCcrSubmission, fetchPurchaseRequestSubmission } from '../../services/notifications';
import { formatDateTime } from "../../utilities/globalServiceUtils";
import { useNotifications } from '../../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getNotificationNavigationTarget } from '../../utilities/notificationNavigation';
import { useSite } from '../../context/siteContext';
import Swal from 'sweetalert2';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { ROUTES } from '../../constants';
import { useCart } from '../../context/cartContext';
import { useAuth } from '../../context/authContext';
import { saveRequestListBulk } from '../../services/rsplService';
import { getProductsById } from '../../services/cartService';
import { NotificationEvents } from '../../constants/notificationEvents';
import formatCurrency from "../../utilities/currency";
import { ASSETS_BASE_URL } from "../../config";
import useBrandingAssets from "../../hooks/useBrandingAssets";

const buildPurchaseRequestCardSummary = (notification = {}) => {
	const data = notification?.data || {};
	let requestedBy = String(data?.requested_by_name || "").trim();
	if (!requestedBy) {
		const body = String(notification?.body || "");
		const match = body.match(/^(.+?)\s+requested help placing an order\./i);
		requestedBy = String(match?.[1] || "").trim();
	}
	const message = String(data?.message || "").trim();
	const items = Array.isArray(data?.items) ? data.items : [];
	const productCount = items.length;
	const totalFromData = String(data?.total_amount || "").trim();
	let total = totalFromData;
	if (!total) {
		const msg = message || String(notification?.body || "");
		const m = msg.match(/total\s*amount\s*:\s*([^\.\n]+)/i);
		if (m?.[1]) total = m[1].trim();
	}

	const parts = [];
	if (requestedBy) parts.push(`Requested by: ${requestedBy}`);
	if (message) parts.push(`Message: ${message}`);
	if (productCount > 0) parts.push(`Parts: ${productCount}`);
	if (total) parts.push(`Total: ${total}`);
	return parts.join(" • ");
};

const getPurchaseRequestSender = (notification = {}, submission = null) => {
	const bySubmission = String(submission?.requested_by?.user_name || "").trim();
	if (bySubmission) return bySubmission;
	const byData = String(notification?.data?.requested_by_name || "").trim();
	if (byData) return byData;
	const body = String(notification?.body || "");
	const match = body.match(/^(.+?)\s+requested help placing an order\./i);
	return String(match?.[1] || "").trim();
};

const NotificationBell = () => {
	const { items, unread, lastNewEventToken, markAllRead, markOneRead, markOneUnread, deleteRecipients, refreshNotifications } = useNotifications();
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const [showNew, setShowNew] = React.useState(false);
	const buttonRef = React.useRef(null);
	const arrowRef = React.useRef(null);
	const [busy, setBusy] = React.useState(false);
	const [sortOrder, setSortOrder] = React.useState(t('GEOGRAPHIC.NEWEST_FIRST'));
	const [tab, setTab] = React.useState('unread');
	const [ccrModalOpen, setCcrModalOpen] = React.useState(false);
	const [ccrSubmission, setCcrSubmission] = React.useState(null);
	const [purchaseRequestModalOpen, setPurchaseRequestModalOpen] = React.useState(false);
	const [activePurchaseRequestNotification, setActivePurchaseRequestNotification] = React.useState(null);
	const [purchaseRequestSubmission, setPurchaseRequestSubmission] = React.useState(null);
	const [purchaseRequestLoading, setPurchaseRequestLoading] = React.useState(false);
	const [purchaseRequestProductMeta, setPurchaseRequestProductMeta] = React.useState({});
	const [addingToCart, setAddingToCart] = React.useState(false);
	const [addingToRequestList, setAddingToRequestList] = React.useState(false);
	const [clearedIds, setClearedIds] = React.useState(() => {
		try {
			const raw = localStorage.getItem('clearedNotificationRecipientIds');
			const parsed = raw ? JSON.parse(raw) : [];
			return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
		} catch {
			return new Set();
		}
	});
	const navigate = useNavigate();
	const { selectedSite, selectedSiteCurrency, siteData, updateSite } = useSite();
	const { stockImageUrl } = useBrandingAssets();
	const { addMultipleToCart } = useCart();
	const { user } = useAuth();
	const newNotificationText = t('UI_TEXT.NEW_NOTIFICATION_POPUP', { defaultValue: 'You have a new notification.' });

	React.useEffect(() => {
		if (lastNewEventToken) {
			setShowNew(true);
			const t = setTimeout(() => setShowNew(false), 3000);
			return () => clearTimeout(t);
		}
	}, [lastNewEventToken]);

	const refresh = async () => {
		try {
			setBusy(true);
			await refreshNotifications(500, 0);
		} finally {
			setBusy(false);
		}
	};

	const toggle = async () => {
		const nxt = !open;
		setOpen(nxt);
		if (nxt) {
			await refresh();
		}
	};

	React.useEffect(() => {
		if (!open) return;
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab, open]);

	const base = items;
	const sortedNotifications = React.useMemo(() => {
		const arr = Array.isArray(base) ? [...base] : [];
		arr.sort((a, b) => {
			const da = new Date(a.created_at).getTime();
			const db = new Date(b.created_at).getTime();
			return sortOrder === t('GEOGRAPHIC.NEWEST_FIRST') ? (db - da) : (da - db);
		});
		return arr.filter(n => {
			const id = n.recipient_id || n.id;
			return id ? !clearedIds.has(String(id)) : true;
		});
	}, [base, sortOrder, clearedIds, t]);

	const unreadTabCount = React.useMemo(
		() => sortedNotifications.filter((n) => !n.read_at).length,
		[sortedNotifications]
	);

	const readTabCount = React.useMemo(
		() => sortedNotifications.filter((n) => !!n.read_at).length,
		[sortedNotifications]
	);

	const dataToShow = React.useMemo(() => {
		const filtered = sortedNotifications.filter((n) => (tab === 'unread' ? !n.read_at : !!n.read_at));
		return filtered.slice(0, 50);
	}, [sortedNotifications, tab]);

	const latestNotification = React.useMemo(() => {
		const arr = Array.isArray(base) ? [...base] : [];
		arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
		return arr[0] || null;
	}, [base]);

	const getIconForType = (type) => {
		const map = {
			ADMIN_NEW_ORDER_PLACED: { icon: <ShoppingCart />, color: 'success' },
			USER_ORDER_SUCCESS: { icon: <ShoppingCart />, color: 'success' },
			ADMIN_NOTIFY_CUSTOMER_CARE: { icon: <SupportAgent />, color: 'secondary' },
			ADMIN_NEW_USER_REGISTERED: { icon: <People />, color: 'primary' },
			USER_FORGOT_PASSWORD: { icon: <WarningAmber />, color: 'warning' },
			USER_REQUESTLIST_PURCHASABLE: { icon: <InfoOutlined />, color: 'success' },
		};
		return map[type] || { icon: <InfoIcon color="action" />, color: 'default' };
	};

	const onMarkOne = async (recipient_id) => {
		if (!recipient_id) return;
		try { await markOneRead(recipient_id); } catch { }
	};

	const onMarkUnread = async (recipient_id) => {
		if (!recipient_id) return;
		try { await markOneUnread(recipient_id); } catch { }
	};

	const onClear = async (recipient_id) => {
		if (!recipient_id) return;
		try {
			await deleteRecipients([recipient_id]);
		} catch { }
		setClearedIds((prev) => {
			const next = new Set(prev);
			next.add(String(recipient_id));
			try {
				localStorage.setItem('clearedNotificationRecipientIds', JSON.stringify(Array.from(next)));
			} catch { }
			return next;
		});
	};

	const formatTime = (ts) => {
		try {
			const d = new Date(ts);
			const now = new Date();
			const diffMs = now - d;
			const sec = Math.floor(diffMs / 1000);
			if (sec < 60) return 'now';
			const min = Math.floor(sec / 60);
			if (min < 60) return `${min} mins ago`;
			const hr = Math.floor(min / 60);
			// yesterday check
			const y = new Date(now);
			y.setDate(now.getDate() - 1);
			const isYesterday = d.getDate() === y.getDate() && d.getMonth() === y.getMonth() && d.getFullYear() === y.getFullYear();
			if (isYesterday) return `yesterday at ${formatDateTime(d)}`;
			if (hr < 24) return `${hr} hours ago`;
			return formatDateTime(d);
		} catch {
			return String(ts);
		}
	};
	const resolveNotificationSiteNumber = React.useCallback((notification) => {
		const data = notification?.data || {};
		const siteNumber =
			data.site_number ||
			data.siteNumber ||
			data.site ||
			data.site_code ||
			data.siteCode ||
			null;
		if (siteNumber) return String(siteNumber);
		const siteId =
			data.site_id ||
			data.siteId ||
			data.site_id_fk ||
			null;
		if (!siteId || !Array.isArray(siteData)) return null;
		const match = siteData.find((s) => Number(s.site_id) === Number(siteId));
		return match?.customer_site_number ? String(match.customer_site_number) : null;
	}, [siteData]);

	const hasAccessToNotificationSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite) return true;
		if (!Array.isArray(siteData) || siteData.length === 0) return false;
		return siteData.some((s) => {
			const siteNum = s?.customer_site_number || s?.site_number || s?.site || s?.site_code;
			return siteNum && String(siteNum) === String(notifSite);
		});
	}, [resolveNotificationSiteNumber, siteData]);

	const isDifferentSite = React.useCallback((notification) => {
		const notifSite = resolveNotificationSiteNumber(notification);
		if (!notifSite || !selectedSite) return false;
		return String(notifSite) !== String(selectedSite);
	}, [resolveNotificationSiteNumber, selectedSite]);

	const isPurchaseRequestNotification = React.useCallback((notification) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		return eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
	}, []);

	const resolvePurchaseRequestSiteNumber = React.useCallback((notification, submission = null) => {
		const fromSubmission = submission?.site?.site_number;
		if (fromSubmission) return String(fromSubmission);
		return resolveNotificationSiteNumber(notification);
	}, [resolveNotificationSiteNumber]);

	const resolveProductImageSrc = React.useCallback((item = {}) => {
		const raw = item?.asset_data?.images?.[0];
		if (raw) {
			if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
			const imgPath = raw.startsWith("/") ? raw : `/${raw}`;
			return `${ASSETS_BASE_URL}${imgPath}`;
		}
		return `${ASSETS_BASE_URL}/ProductImages/${item.product_number}.png`;
	}, []);

	const getPurchaseRequestItems = React.useCallback((notification, submission = null) => {
		const rawItems = submission?.products || notification?.data?.items;
		if (!Array.isArray(rawItems)) return [];
		return rawItems
			.map((item) => {
				const productId = Number(item?.product_id);
				const quantity = Number(item?.quantity || 1);
				const machineId = Number(item?.machine_id);
				const meta = purchaseRequestProductMeta?.[productId] || {};
				if (!Number.isFinite(productId) || productId <= 0) return null;
				if (!Number.isFinite(quantity) || quantity <= 0) return null;
				return {
					product_id: productId,
					machine_id: Number.isFinite(machineId) && machineId > 0 ? machineId : (Number(meta?.machine_id) || null),
					product_number: String(item?.product_number || meta?.product_number || "").trim(),
					product_name: String(item?.product_name || meta?.product_name || "").trim(),
					asset_data: meta?.asset_data || null,
					quantity,
					purchasable: true,
					unit_price: Number(meta?.[`price_${selectedSiteCurrency}`] ?? 0) || 0,
				};
			})
			.filter(Boolean);
	}, [purchaseRequestProductMeta, selectedSiteCurrency]);

	const handleOpenPurchaseRequestNotification = React.useCallback(async (notification) => {
		setActivePurchaseRequestNotification(notification || null);
		setPurchaseRequestSubmission(null);
		setPurchaseRequestProductMeta({});
		setPurchaseRequestModalOpen(true);

		const submissionId = notification?.data?.purchase_request_submission_id;
		setPurchaseRequestLoading(true);
		try {
			let submission = null;
			if (submissionId) {
				submission = await fetchPurchaseRequestSubmission(submissionId);
				if (submission) setPurchaseRequestSubmission(submission);
			}
			const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission);
			const rows = submission?.products || notification?.data?.items || [];
			const productIds = Array.from(
				new Set(
					(Array.isArray(rows) ? rows : [])
						.map((item) => Number(item?.product_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			);
			if (siteNumber && productIds.length) {
				const productsResp = await getProductsById(productIds, siteNumber);
				const products = Array.isArray(productsResp?.data) ? productsResp.data : [];
				const metaMap = {};
				products.forEach((product) => {
					const id = Number(product?.product_id);
					if (Number.isFinite(id) && id > 0) metaMap[id] = product;
				});
				setPurchaseRequestProductMeta(metaMap);
			}
		} finally {
			setPurchaseRequestLoading(false);
		}
	}, [resolvePurchaseRequestSiteNumber]);

	const ensurePurchaseRequestSiteReady = React.useCallback(async (notification, submission = null) => {
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return null;
		}
		const notifSite = resolvePurchaseRequestSiteNumber(notification, submission);
		if (!notifSite) return null;
		const shouldPrompt = notifSite && selectedSite && String(notifSite) !== String(selectedSite);
		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This action will apply to the notification site.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonText: t('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return null;
			updateSite(String(notifSite), siteData || []);
		}
		return String(notifSite);
	}, [hasAccessToNotificationSite, resolvePurchaseRequestSiteNumber, selectedSite, siteData, t, updateSite]);

	const handleAddPurchaseRequestItemsToCart = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification || !items.length) return;
		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to quote?',
			text: `Add ${items.length} item(s) to quote for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Quote',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToCart(true);
		try {
			const products = items.map((item) => ({
				ProductID: item.product_id,
				Quantity: Number(item.quantity) || 1,
				Site: siteNumber,
			}));
			await addMultipleToCart(products);
		} finally {
			setAddingToCart(false);
		}
	}, [activePurchaseRequestNotification, addMultipleToCart, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t]);

	const handleAddPurchaseRequestItemsToRequestList = React.useCallback(async (items) => {
		const notification = activePurchaseRequestNotification;
		if (!notification || !items.length) return;
		const missingMachine = items.filter((item) => !item.machine_id);
		if (missingMachine.length > 0) {
			await Swal.fire({
				icon: 'warning',
				title: 'Missing machine mapping',
				text: `${missingMachine.length} item(s) cannot be added to request list because machine_id is missing.`,
			});
			return;
		}
		const siteNumber = await ensurePurchaseRequestSiteReady(notification, purchaseRequestSubmission);
		if (!siteNumber) return;

		const confirm = await Swal.fire({
			icon: 'question',
			title: 'Add to request list?',
			text: `Add ${items.length} item(s) to request list for site ${siteNumber}?`,
			showCancelButton: true,
			confirmButtonText: 'Yes, Add to Request List',
			cancelButtonText: t('BUTTON.CANCEL'),
		});
		if (!confirm.isConfirmed) return;

		setAddingToRequestList(true);
		try {
			const payload = items.map((item) => ({
				user_id: user?.user_id,
				site_number: siteNumber,
				machine_id: Number(item.machine_id),
				product_id: Number(item.product_id),
			}));
			await saveRequestListBulk(payload);
		} finally {
			setAddingToRequestList(false);
		}
	}, [activePurchaseRequestNotification, ensurePurchaseRequestSiteReady, purchaseRequestSubmission, t, user?.user_id]);

	const handleOrderPurchaseRequestItems = React.useCallback(async (items) => {
		const purchasableItems = items.filter(Boolean);
		if (!purchasableItems.length) {
			return;
		}
		await handleAddPurchaseRequestItemsToCart(purchasableItems);
		setOpen(false);
		navigate(ROUTES.CART);
	}, [handleAddPurchaseRequestItemsToCart, handleAddPurchaseRequestItemsToRequestList, navigate, t]);

	const handleNotificationRedirect = React.useCallback(async (notification, explicitTarget) => {
		const eventKey = String(notification?.event_key || "").toUpperCase();
		const isPurchaseRequest = eventKey === NotificationEvents.USER_PURCHASE_REQUEST_SUBMITTED;
		const isCcrEvent = eventKey.includes("CONTACT_CCR");
		if (!notification?.read_at && notification?.recipient_id) {
			await onMarkOne(notification.recipient_id);
		}
		if (isPurchaseRequest) {
			await handleOpenPurchaseRequestNotification(notification);
			return;
		}
		if (isCcrEvent) {
			setCcrSubmission(null);
			setCcrModalOpen(true);
			const submissionId = notification?.data?.ccr_submission_id;
			if (submissionId) {
				try {
					const submission = await fetchCcrSubmission(submissionId);
					if (submission) {
						setCcrSubmission(submission);
					}
				} catch {
					// keep modal open with empty state
				}
			}
			return;
		}
		if (!hasAccessToNotificationSite(notification)) {
			const notifSite = resolveNotificationSiteNumber(notification);
			await Swal.fire({
				icon: 'warning',
				title: 'Access removed',
				text: `You no longer have access to the ${notifSite || 'selected'} location. Please contact system administrator for further details.`,
				toast: true,
				position: 'top-end',
				timer: 4000,
				showConfirmButton: false,
			});
			return;
		}
		const target = explicitTarget || getNotificationNavigationTarget(notification);
		if (!target || target === ROUTES.SUPPORT) return;
		const notifSite = resolveNotificationSiteNumber(notification);
		const shouldPrompt = notifSite && selectedSite && String(notifSite) !== String(selectedSite) && !/^https?:/i.test(target);
		if (shouldPrompt) {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Switch Customer Location?',
				text: 'This will navigate the user to a different site, the site where this order was placed from.',
				showCancelButton: true,
				confirmButtonText: 'Yes, Switch Customer Location',
				cancelButtonTextt: ('BUTTON.CANCEL'),
			});
			if (!result.isConfirmed) return;
			updateSite(String(notifSite), siteData || []);
		}
		setOpen(false);
		if (/^https?:/i.test(target)) {
			window.location.href = target;
		} else {
			navigate(target);
		}
	}, [handleOpenPurchaseRequestNotification, hasAccessToNotificationSite, navigate, onMarkOne, resolveNotificationSiteNumber, selectedSite, siteData, updateSite]);

	const handleClearRead = React.useCallback(async () => {
		const baseAll = items || [];
		const readIds = baseAll
			.filter(n => !!n.read_at)
			.map(n => n.recipient_id || n.id)
			.filter(Boolean)
			.map(String);
		if (!readIds.length) return;
		try {
			await deleteRecipients(readIds);
		} catch { }
		setClearedIds(prev => {
			const next = new Set(prev);
			readIds.forEach(id => next.add(id));
			try {
				localStorage.setItem('clearedNotificationRecipientIds', JSON.stringify(Array.from(next)));
			} catch { }
			return next;
		});
	}, [deleteRecipients, items]);

	return (
		<Box sx={{ position: 'relative' }}>
			<ModalWrapper
				open={ccrModalOpen}
				setOpen={setCcrModalOpen}
				title={t('GENERAL.CONTACT_CCR_SUBMISSION')}
				contentWidth={720}
				content={<>
					{ccrSubmission ? (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
								<Box sx={{ minWidth: 200 }}>
									<Typography variant="caption" color="text.secondary">Submitted</Typography>
									<Typography variant="body2">{formatDateTime(ccrSubmission.created_at)}</Typography>
								</Box>
								{ccrSubmission.site_number && (
									<Box sx={{ minWidth: 200 }}>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{ccrSubmission.site_name} - {ccrSubmission.site_number}</Typography>
									</Box>
								)}
							</Box>
							<Divider />
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
								<Box>
									<Typography variant="caption" color="text.secondary">Name</Typography>
									<Typography variant="body2">{ccrSubmission.name || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('TABLE_HEADERS.EMAIL')}</Typography>
									<Typography variant="body2">{ccrSubmission.email || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">{t('LABELS.PHONE')}</Typography>
									<Typography variant="body2">{ccrSubmission.phone || '-'}</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Machine Number</Typography>
									<Typography variant="body2">{ccrSubmission.machine_number || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">Subject</Typography>
									<Typography variant="body2">{ccrSubmission.subject || '-'}</Typography>
								</Box>
								<Box sx={{ gridColumn: '1 / -1' }}>
									<Typography variant="caption" color="text.secondary">Message</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{ccrSubmission.message || '-'}</Typography>
								</Box>
							</Box>
						</Box>
					) : (
						<Alert severity="info">{t('ALERT_MESSAGES.NO_DETAILS_FOUND_FOR_SUBMISSION')}</Alert>
					)}
				</>}
			/>
			<ModalWrapper
				open={purchaseRequestModalOpen}
				setOpen={setPurchaseRequestModalOpen}
				title="Purchase Request"
				subtitle="Review request details and requested parts."
				width="720px"
				content={
					(() => {
						const notification = activePurchaseRequestNotification;
						const submission = purchaseRequestSubmission;
						const data = notification?.data || {};
						const items = getPurchaseRequestItems(notification, submission);
						const recipients = Array.isArray(submission?.recipients) ? submission.recipients : [];
						const siteNumber = resolvePurchaseRequestSiteNumber(notification, submission) || '-';
						const siteName = submission?.site?.site_name || data?.site_name || '-';
						const requestedBy = getPurchaseRequestSender(notification, submission) || '-';
						return (
							<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
								{purchaseRequestLoading && (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
										<CircularProgress size={22} />
									</Box>
								)}
								<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested By</Typography>
										<Typography variant="body2">{requestedBy}</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Site</Typography>
										<Typography variant="body2">{siteName} ({siteNumber})</Typography>
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary">Requested At</Typography>
										<Typography variant="body2">{notification?.created_at ? formatDateTime(notification.created_at) : '-'}</Typography>
									</Box>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Message</Typography>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
										{String(submission?.message || data?.message || notification?.body || '-')}
									</Typography>
								</Box>
								<Box>
									<Typography variant="caption" color="text.secondary">Recipients</Typography>
									{recipients.length ? (
										<Stack spacing={0.5}>
											{recipients.map((recipient, idx) => (
												<Typography key={`${recipient.user_id || idx}`} variant="body2">
													{recipient.user_name || recipient.user_email || `User ${recipient.user_id}`} {recipient.user_email ? `(${recipient.user_email})` : ''}
												</Typography>
											))}
										</Stack>
									) : <Typography variant="body2">-</Typography>}
								</Box>
								<Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
									<Typography variant="subtitle2" sx={{ mb: 1 }}>Requested Parts ({items.length})</Typography>
									{items.length ? (
										<TableContainer sx={{ maxHeight: 320 }}>
											<Table size="small" stickyHeader>
												<TableHead>
													<TableRow>
														<TableCell>Part Name</TableCell>
														<TableCell align="right">Quantity</TableCell>
														<TableCell align="right">Price</TableCell>
														<TableCell align="right">Total</TableCell>
														<TableCell align="right">Actions</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{items.map((item, index) => (
														<TableRow key={`${item.product_id}-${index}`}>
															<TableCell>
																<Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
																	<img
																		src={resolveProductImageSrc(item)}
																		alt={item.product_name || item.product_number || "Part"}
																		onError={(event) => {
																			event.currentTarget.onerror = null;
																			event.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
																		}}
																		style={{ width: 28, height: 28, objectFit: "contain" }}
																	/>
																	<Box sx={{ minWidth: 0 }}>
																		{item.product_id && item.machine_id ? (
																			<Link
																				underline="hover"
																				sx={{
																					cursor: "pointer",
																					fontSize: "0.9rem",
																					display: "block",
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																				onClick={() => navigate(`/machines/${item.machine_id}/${item.product_id}`)}
																			>
																				{item.product_name || '-'}
																			</Link>
																		) : (
																			<Typography
																				variant="body2"
																				sx={{
																					maxWidth: 220,
																					whiteSpace: "nowrap",
																					overflow: "hidden",
																					textOverflow: "ellipsis",
																				}}
																				title={item.product_name || "-"}
																			>
																				{item.product_name || '-'}
																			</Typography>
																		)}
																		<Typography
																			variant="caption"
																			color="text.secondary"
																			sx={{
																				display: "block",
																				maxWidth: 220,
																				whiteSpace: "nowrap",
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																			}}
																			title={`Part #: ${item.product_number || item.product_id}`}
																		>
																			Part #: {item.product_number || item.product_id}
																		</Typography>
																	</Box>
																</Box>
															</TableCell>
															<TableCell align="right">x {item.quantity}</TableCell>
															<TableCell align="right">{/* {formatCurrency(item.unit_price || 0, selectedSiteCurrency)} */}</TableCell>
															<TableCell align="right">{/* {formatCurrency((item.unit_price || 0) * Number(item.quantity || 0), selectedSiteCurrency)} */}</TableCell>
															<TableCell align="right">
																<Tooltip title="Add to Quote">
																	<span>
																		<IconButton
																			color="primary"
																			size="small"
																			onClick={() => {
																				handleAddPurchaseRequestItemsToCart([item]);
																			}}
																			disabled={addingToCart || addingToRequestList}
																		>
																			<AddShoppingCart fontSize="small" />
																		</IconButton>
																	</span>
																</Tooltip>
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										</TableContainer>
									) : (
										<Alert severity="info">No valid items in this request.</Alert>
									)}
								</Box>
							</Box>
						);
					})()
				}
				actions={
					<>
						<Button variant="outlined" onClick={() => setPurchaseRequestModalOpen(false)} disabled={addingToCart || addingToRequestList}>
							{t('BUTTON.CANCEL')}
						</Button>
						<Button
							variant="contained"
							onClick={() => handleOrderPurchaseRequestItems(getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission))}
							disabled={addingToCart || addingToRequestList || !getPurchaseRequestItems(activePurchaseRequestNotification, purchaseRequestSubmission).length}
						>
							{addingToCart || addingToRequestList ? 'Processing...' : 'Quote Items'}
						</Button>
					</>
				}
			/>


			<Box sx={{ position: 'relative', display: 'inline-block' }}>
				<Tooltip
					title={showNew ? '' : (unread > 0 ? `${t('UI_TEXT.YOU_HAVE')} ${unread} ${t('UI_TEXT.UNREAD_NOTIFICATION')}${unread > 1 ? 's' : ''}` : t('UI_TEXT.NO_UNREAD_NOTIFICATIONS'))}
					arrow>
					<Badge color="error" variant="dot" invisible={unread <= 0}>
						<Button
							ref={buttonRef}
							onClick={toggle}
							color="primary"
							variant="roundedGreyBgSquareContained"
						>
							{unread > 0 ? (
							<Notifications sx={{ fontSize: '20px' }} />
							) : (
								<NotificationsNoneOutlined sx={{ fontSize: '20px' }} />
							)}
						</Button>
					</Badge>
				</Tooltip>
			</Box>

			{/* Popup */}
			<Popper
				open={showNew && !open}
				anchorEl={buttonRef.current}
				placement="bottom-end"
				transition
				sx={{ zIndex: 1300 }}
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={250}>
						<Paper
							onClick={toggle}
							sx={{
								p: 1.5,
								mt: '5px',
								boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
								borderRadius: 2,
								bgcolor: 'background.paper',
								color: 'text.primary',
								position: 'relative',
								minWidth: 220,
								cursor: 'pointer',
							}}
						>
							<Stack direction="row" spacing={1} alignItems="center">
								<Notifications color="primary" />
								<Typography variant="body1" fontWeight={"bold"}>
									{newNotificationText}
								</Typography>
								{latestNotification && isDifferentSite(latestNotification) && (
									<Chip size="small" sx={{ height: 18, fontSize: 10 }} color="warning" label="Different Customer Location" />
								)}
							</Stack>
							{latestNotification && isDifferentSite(latestNotification) && (
								<Typography variant="caption" color="text.secondary">
									{`Site: ${resolveNotificationSiteNumber(latestNotification)}`}
								</Typography>
							)}
							<Box
								ref={arrowRef}
								sx={{
									position: 'absolute',
									width: 12,
									height: 12,
									top: -6,
									right: 10,
									bgcolor: 'background.paper',
									transform: 'rotate(45deg)',
									boxShadow: '-2px -2px 7px 0px rgba(0, 0, 0, 0.1)',
									zIndex: 1,
								}}
							/>
						</Paper>
					</Fade>
				)}
			</Popper>

			<Drawer
				anchor="right"
				open={open}
				onClose={() => setOpen(false)}
				PaperProps={{
					sx: {
						width: { xs: "100vw", sm: "85vw", md: 420, lg: "28vw" },
						maxWidth: { xs: "100vw", sm: 520 },
						minWidth: 300,
						height: "100dvh",
						maxHeight: "100dvh",
						overflow: "hidden",
						p: { xs: 0.75, sm: 1 },
						display: "flex",
						flexDirection: "column",
						zIndex: 1053
					},
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 1, pt: 1, gap: 1 }}>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', minWidth: 0, rowGap: 1, flex: 1 }}>
						<Typography variant="h6" fontWeight={'bold'}>{t('UI_TEXT.NOTIFICATIONS_1')}</Typography>
						<Chip size="small" color={unreadTabCount > 0 ? 'warning' : 'default'} label={unreadTabCount > 0 ? `${unreadTabCount} ${t('UI_TEXT.UNREAD_1')}` : t('GEOGRAPHIC.ALL_CAUGHT_UP')} />
						<Chip size="small" variant="outlined" label={sortOrder} />
					</Stack>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
						<Tooltip title={sortOrder === t('GEOGRAPHIC.NEWEST_FIRST') ? t('GENERAL.SORT_OLDEST_FIRST') : t('GENERAL.SORT_NEWEST_FIRST')}>
							<Button variant="roundedGreyBgSquareContained" onClick={() => setSortOrder(prev => prev === t('GEOGRAPHIC.NEWEST_FIRST') ? t('GEOGRAPHIC.OLDEST_FIRST') : t('GEOGRAPHIC.NEWEST_FIRST'))}>
								<SortIcon />
							</Button>
						</Tooltip>
						<Tooltip title={t('UI_TEXT.VIEW_ALL_NOTIFICATIONS', { defaultValue: 'View all notifications' })}>
							<Button
								variant="roundedGreyBgSquareContained"
								onClick={() => {
									setOpen(false);
									navigate(ROUTES.NOTIFICATIONS);
								}}
							>
								<VisibilityIcon />
							</Button>
						</Tooltip>
						<IconButton size="small" onClick={() => setOpen(false)} aria-label="Close notifications">
							<CloseIcon />
						</IconButton>
					</Stack>
				</Box>
				<Divider sx={{ my: 1 }} />
				<Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ px: 1 }}>
					<Tab label={`${t('UI_TEXT.UNREAD_1')}${unreadTabCount > 0 ? ` (${unreadTabCount})` : ''}`} value="unread" />
					<Tab label={`${t('GENERAL.READ_2')}${readTabCount > 0 ? ` (${readTabCount})` : ''}`} value="read" />
				</Tabs>

				<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: { xs: 0, sm: 1 }, pb: 1 }}>
					{dataToShow.length > 0 ? (
						<List dense sx={{ width: '100%' }}>
							{dataToShow.map((n, idx) => {
								const meta = getIconForType(n.event_key);
								const isRead = !!n.read_at;
								const canAccess = hasAccessToNotificationSite(n);
								const route = canAccess ? getNotificationNavigationTarget(n) : null;
								const isPurchaseRequest = isPurchaseRequestNotification(n);
								const isClickable = Boolean(route || isPurchaseRequest);
								const differentSite = isDifferentSite(n);
								const notifSite = resolveNotificationSiteNumber(n);
								return (
									<ListItem key={n.recipient_id || n.id || idx} disablePadding secondaryAction={
										<Stack direction="row" spacing={0.5} alignItems="center">
											{isRead ? (
												<Tooltip title={t('UI_TEXT.MARK_AS_UNREAD')}>
													<span>
														<IconButton edge="end" size="small" onClick={() => onMarkUnread(n.recipient_id)} disabled={!n.recipient_id}>
															<MarkEmailUnreadIcon fontSize="small" color={'primary'} />
														</IconButton>
													</span>
												</Tooltip>
											) : (
												<Tooltip title={t('UI_TEXT.MARK_AS_READ')}>
													<span>
														<IconButton edge="end" size="small" onClick={() => onMarkOne(n.recipient_id)} disabled={!n.recipient_id}>
															<MarkEmailReadIcon fontSize="small" color={'success'} />
														</IconButton>
													</span>
												</Tooltip>
											)}
											{tab === 'read' && (
												<Tooltip title={'Clear'}>
													<span>
														<IconButton edge="end" size="small" onClick={() => onClear(n.recipient_id)} disabled={!n.recipient_id}>
															<ClearIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											)}
										</Stack>
									}>
										<ListItemButton
											onClick={() => handleNotificationRedirect(n, route)}
											disableRipple={!isClickable}
											sx={{
												alignItems: 'flex-start',
												cursor: isClickable ? 'pointer' : 'default',
												py: 1,
												borderLeft: differentSite ? '3px solid #f59e0b' : '3px solid transparent',
												backgroundColor: differentSite ? 'rgba(245,158,11,0.08)' : 'transparent',
											}}
										>
											<ListItemIcon sx={{ minWidth: 44, mt: 0.3 }}>
												<Button variant="roundedGreyBgSquareContained" size="small">
													{meta.icon}
												</Button>
											</ListItemIcon>
											<ListItemText
												primary={
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
														<Typography variant="body2" fontWeight={600} sx={{ color: isRead ? 'text.secondary' : 'text.primary' }}>{n.title || n.event_key}</Typography>

													</Box>
												}
												secondary={
													(() => {
														const desc = n.description || (n.data && n.data.description) || '';
														const eventKey = String(n?.event_key || "").toUpperCase();
														const isPurchaseRequest = eventKey === "USER_PURCHASE_REQUEST_SUBMITTED";
														const parts = [];
														if (desc) parts.push(desc);
														if (isPurchaseRequest) {
															const compact = buildPurchaseRequestCardSummary(n);
															if (compact) parts.push(compact);
														} else if (n.body) {
															parts.push(n.body);
														}
														//if (differentSite && notifSite) parts.push(`Site: ${notifSite}`);
														parts.push(formatTime(n.created_at));

														return (
															<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '95%' }}>
																<Typography variant="caption" color="text.secondary">
																	{parts.filter(Boolean).join(' • ')}
																</Typography>
																{differentSite && (
																	<Typography fontSize="11px" fontStyle="italic" color="warning.main">
																		{(() => {
																			const siteMatch = siteData?.find(
																				(s) => String(s?.customer_site_number) === String(notifSite)
																			);
																			const parts = ['Different Customer Location'];
																			if (siteMatch?.parent_customer_number) parts.push(`Site No : ${siteMatch.parent_customer_number}`);
																			if (siteMatch?.parent_customer_name) parts.push(siteMatch.parent_customer_name);
																			return parts.join(' • ');
																		})()}
																	</Typography>
																)}
															</Box>
														);
													})()
												}
											/>
										</ListItemButton>
									</ListItem>
								);
							})}
						</List>
					) : (
						<Box sx={{ px: 2, mt: 1 }}>
							<Alert severity="info">{t('UI_TEXT.NO_NOTIFICATIONS_YET')}</Alert>
						</Box>
					)}
				</Box>
				<Divider sx={{ mt: 1 }} />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						rowGap: 1,
						px: 1,
						py: 1,
					}}
				>
					<Button variant="outlined" onClick={() => setOpen(false)} startIcon={<CloseIcon />}>{t('BUTTON.CLOSE')}</Button>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
						{tab === 'read' ? (
							<Button variant="contained" onClick={handleClearRead} startIcon={<MarkEmailReadIcon />}>
								{t('UI_TEXT.CLEAR_READ')}
							</Button>
						) : (
							<>
								<Button variant="contained" onClick={refresh} disabled={busy} startIcon={<RefreshIcon />}>{t("BUTTON.REFRESH")}</Button>
								<Button
									variant="contained"
									onClick={async () => {
										const baseAll = items || [];
										const ids = Array.from(new Set(baseAll.filter(n => !n.read_at && n.recipient_id).map(n => n.recipient_id)));
										if (ids.length) {
											try {
												await markAllRead();
											} catch { }
										}
										await refresh();
									}}
									disabled={unread === 0}
									startIcon={<MarkEmailReadIcon />}
								>
									{t('UI_TEXT.MARK_ALL_READ')}
								</Button>
							</>
						)}
					</Box>
				</Box>

			</Drawer>
		</Box>
	);
};

export default NotificationBell;
